import { LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import Errors from '../ltn-helpers/errors.js';

export enum AuthState {
  AUTH_REQUIRED,
  AUTHENTICATED,
  LOADING,
  ERROR,
}

interface AuthResponseUser {
  id: string;
  person: {
    id: string;
    address: string;
    authId: string;
    avatar: string;
    company: string;
    createdAt: string;
    emails: string[];
    forename: string;
    formalName: string;
    initials: string;
    name: string;
    phone: { landline: string; mobile: string };
    postcode: string;
    role: string;
    suffix: string;
    surname: string;
    title: string;
    updatedAt: string;
  };
  policyProperties: { [index: string]: string };
  token?: string;
  tokens: Array<string>;
}
interface AuthResponse {
  user: AuthResponseUser;
  data: unknown;
}

@customElement('ltn-auth-service')
export class LtnAuthService extends LitElement {
  public state: AuthState = AuthState.AUTH_REQUIRED;

  private _user?: AuthResponseUser | null;

  private _token?: string;

  private _data?: unknown;

  validateTokenURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/user';

  authenticateURL: string = '%{BUTTRESS_URL}%/lambda/v1/%{BUTTRESS_API_PATH}%/auth/google/authenticate';

  redirect: boolean = true;

  async authenticate(url?: string, tokenURL?: string, redirect: boolean = true) {
    this.authenticateURL = url || this.authenticateURL;
    this.validateTokenURL = tokenURL || this.validateTokenURL;
    this.redirect = redirect;

    if (this.state === AuthState.LOADING) {
      throw new Error('Already authenticating');
    }

    return this.__generateAuthenticatedRequest();
  }

  async authErrors() {
    this.__changeState(AuthState.LOADING);

    // TODO return one time auth code from buttress instead of user token!!
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const code = params.get('code');

    // Only reset the history entry if we've got the error code in the url
    if (code) {
      params.delete('code');
      window.history.replaceState({}, '', url.toString());
    }

    this.__changeState(AuthState.AUTH_REQUIRED);
    return code;
  }

  async validateToken() {
    this.__changeState(AuthState.LOADING);

    // TODO return one time auth code from buttress instead of user token!!
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const userToken = params.get('userToken') || sessionStorage.getItem('userToken');
    const oauthApp = params.get('oauthApp') || sessionStorage.getItem('oauthApp');

    // Only reset the push state history if we've got the token in the url
    if (params.get('oauthApp')) {
      params.delete('oauthApp');
    }

    if (oauthApp && sessionStorage.getItem('oauthApp') !== oauthApp) {
      sessionStorage.setItem('oauthApp', oauthApp);
    }

    // Replaced, not pushed: taking the token out of the address adds no history entry, so Back can't
    // return to the sign-in landing (or reach the token), and an onboarding redirect replaces the only entry
    if (params.get('userToken')) {
      params.delete('userToken');
      window.history.replaceState({}, '', url.toString());
    }

    if (userToken) {
      if (!sessionStorage.getItem('userToken')) {
        sessionStorage.setItem('userToken', userToken);
      }

      try {
        // In a header, not the body, which Buttress stores with the lambda execution
        const res = await fetch(this.validateTokenURL, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (res.status >= 500) throw new Error(`Token validation failed with status ${res.status}`);
        const data = res.ok ? await res.json() : undefined;
        this.__handleAuthResponse(data?.res, userToken);
      } catch {
        this._user = null;
        this._token = undefined;
        this._data = undefined;
        this.__changeState(AuthState.ERROR);
      }
      return;
    }

    this.__changeState(AuthState.AUTH_REQUIRED);
  }

  async authenticateLambda() {
    if (this.state === AuthState.LOADING) {
      throw new Error('Already authenticating');
    }

    return this.__generateAuthenticatedRequest();
  }

  // subscribe(cb: {(state: AuthState): void;}) {
  //   this._subscriptions.push(cb);
  // }

  // unsubscribe(cb: {(state: AuthState): void;}): void {
  //   const subIdx = this._subscriptions.findIndex((sub) => sub === cb);
  //   if (subIdx) this._subscriptions.splice(subIdx, 1);
  // }

  get user() {
    return this._user;
  }

  get token() {
    return this._token;
  }

  get data() {
    return this._data;
  }

  private __changeState(state: AuthState, notify: boolean = true) {
    this.state = state;
    if (notify) this.dispatchEvent(new CustomEvent('auth-changed', { detail: state }));
  }

  // TODO: Convert to use Lambda, need to make sure the tokens are exchanged via
  //       the request body and avoid using the querystring.

  private async __generateAuthenticatedRequest() {
    this.__changeState(AuthState.LOADING);

    try {
      if (this.redirect) {
        window.location.href = `${this.authenticateURL}`;
      } else {
        const res = await fetch(`${this.authenticateURL}`);
        const data = await res.json();

        if (!sessionStorage.getItem('userToken')) {
          sessionStorage.setItem('userToken', data.res.user.token);
        }

        this.__handleAuthResponse(data.res);
      }
    } catch (err) {
      this.__handleAuthResponse();
      this.__changeState(AuthState.ERROR);

      throw new Errors.ServiceOffline(err as string);
    }
  }

  private __handleAuthResponse(res?: AuthResponse, userToken = res?.user.token) {
    this._user = null;

    if (!res || !res.user.id || !userToken) {
      this._user = null;
      this._token = undefined;
      this._data = undefined;
      this.__changeState(AuthState.AUTH_REQUIRED);
      return;
    }

    this._user = res.user;
    this._token = userToken;
    this._user.token = this._token;
    this._data = res.data;
    this.__changeState(AuthState.AUTHENTICATED);
  }
}
